import React from 'react';
import { Card } from './ui/Card';
import { Badge } from './ui/Badge';
import { Tooltip, TooltipContent, TooltipTrigger } from './ui/Tooltip';
import { Company, SignalType, ReviewBreakdown } from '../data/companies';
import { Star, MessageSquareQuote, Building2, Info } from 'lucide-react';
const TEAMBLIND_LOGO = 'https://cdn.magicpatterns.com/uploads/ig2VK9YG37PmXsDkFrJqFi/image.png';
const GLASSDOOR_LOGO = 'https://cdn.magicpatterns.com/uploads/oXL43uEUBnLtAB8LzmjffR/pasted-image.png';
interface CompanyCardProps {
  company: Company;
}
function getSignalEmoji(text: string): string {
  const lower = text.toLowerCase();
  if (lower.includes('trans healthcare')) return '🏳️‍⚧️';
  if (lower.includes('parental leave')) return '👨‍👩‍👧';
  if (lower.includes('progressive causes') || lower.includes('supports progressive')) return '🤝';
  if (lower.includes('no layoffs')) return '🛡️';
  if (lower.includes('recent layoffs') || lower.includes('layoffs')) return '📉';
  if (lower.includes('anti-dei') || lower.includes('anti-lgbtq') || lower.includes('anti-abortion')) return '🚩';
  if (lower.includes('diverse leadership')) return '🌍';
  if (lower.includes('dei report')) return '📊';
  if (lower.includes('flexible hours')) return '⏰';
  if (lower.includes('remote')) return '🏠';
  if (lower.includes('erg programs')) return '🤲';
  if (lower.includes('partner healthcare')) return '💕';
  if (lower.includes('disability')) return '♿';
  if (lower.includes('pay equity')) return '⚖️';
  if (lower.includes('crunch culture')) return '😰';
  if (lower.includes('toxicity')) return '☢️';
  if (lower.includes('benefits unknown') || lower.includes('no sentiment')) return '❓';
  return '';
}
function getMatchColor(score: number) {
  if (score >= 80) return {
    bg: 'bg-emerald-500',
    text: 'text-white',
    ring: 'ring-emerald-500/20'
  };
  if (score >= 50) return {
    bg: 'bg-amber-500',
    text: 'text-white',
    ring: 'ring-amber-500/20'
  };
  return {
    bg: 'bg-red-500',
    text: 'text-white',
    ring: 'ring-red-500/20'
  };
}
function StarRating({
  rating
}: {
  rating: number;
}) {
  const fullStars = Math.floor(rating);
  const hasHalf = rating - fullStars >= 0.3;
  return <div className="flex items-center gap-px" data-id="element-7">
      {Array.from({
      length: 5
    }).map((_, i) => <Star key={i} className={`w-2.5 h-2.5 ${i < fullStars ? 'fill-amber-400 text-amber-400' : i === fullStars && hasHalf ? 'fill-amber-400/50 text-amber-400' : 'fill-none text-slate-300'}`} data-id="element-8" />)}
    </div>;
}
function RatingBreakdownCard({
  breakdown,
  logo,
  name,
  companyName
}: {
  breakdown: ReviewBreakdown;
  logo: string;
  name: string;
  companyName: string;
}) {
  const categories = [{
    label: 'Career Growth',
    value: breakdown.careerGrowth
  }, {
    label: 'Compensation / Benefits',
    value: breakdown.compensationBenefits
  }, {
    label: 'Management',
    value: breakdown.management
  }, {
    label: 'Work Life Balance',
    value: breakdown.workLifeBalance
  }, {
    label: 'Company Culture',
    value: breakdown.companyCulture
  }];
  return <div className="p-3" data-id="element-9">
      <div className="flex items-center gap-2 mb-3" data-id="element-10">
        <img src={logo} alt={name} className="w-4 h-4 rounded-[2px] object-cover" data-id="element-11" />
        <span className="text-xs font-heading font-semibold text-foreground" data-id="element-12">
          {companyName} — {name} Reviews
        </span>
      </div>
      <div className="flex gap-3" data-id="element-13">
        <div className="flex flex-col items-center border-r border-border/40 pr-3" data-id="element-14">
          <span className="text-2xl font-mono font-bold text-foreground leading-none" data-id="element-15">
            {breakdown.overall}
          </span>
          <span className="text-[10px] font-mono text-muted-foreground mt-1" data-id="element-16">
            {breakdown.reviewCount} Reviews
          </span>
          <div className="mt-1" data-id="element-17">
            <StarRating rating={breakdown.overall} data-id="element-18" />
          </div>
        </div>
        <div className="flex-1 grid grid-cols-2 gap-x-4 gap-y-1.5" data-id="element-19">
          {categories.map(cat => <div key={cat.label} className="flex items-center gap-1.5" data-id="element-20">
              <Star className="w-2.5 h-2.5 fill-amber-400 text-amber-400 shrink-0" data-id="element-21" />
              <span className="text-[11px] font-mono font-semibold text-foreground" data-id="element-22">
                {cat.value}
              </span>
              <span className="text-[10px] text-muted-foreground truncate" data-id="element-23">
                {cat.label}
              </span>
            </div>)}
        </div>
      </div>
    </div>;
}
export function CompanyCard({
  company
}: CompanyCardProps) {
  const getSignalColor = (type: SignalType) => {
    switch (type) {
      case 'positive':
        return 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border-emerald-500/25 hover:bg-emerald-500/20 hover:border-emerald-500/40';
      case 'negative':
        return 'bg-red-500/10 text-red-700 dark:text-red-400 border-red-500/25 hover:bg-red-500/20 hover:border-red-500/40';
      case 'neutral':
        return 'bg-slate-500/10 text-slate-600 dark:text-slate-400 border-slate-500/20 hover:bg-slate-500/20 hover:border-slate-500/30';
    }
  };
  const getSignalPrefix = (type: SignalType) => {
    switch (type) {
      case 'positive':
        return '✓';
      case 'negative':
        return '✗';
      case 'neutral':
        return '—';
    }
  };
  const matchColor = getMatchColor(company.matchScore);
  // Sort signals: positive first, then negative, then neutral
  const sortedSignals = [...company.signals].sort((a, b) => {
    const order = {
      positive: 0,
      negative: 1,
      neutral: 2
    };
    return order[a.type] - order[b.type];
  });
  return <Card className="rounded-md border-border/60 shadow-sm flex flex-col hover:border-border hover:shadow-md transition-all duration-200 bg-card group" data-id="element-24">
      <div className="p-4 flex-1 flex flex-col gap-2.5" data-id="element-25">
        {/* Header Row */}
        <div className="flex justify-between items-start gap-3" data-id="element-26">
          <div className="flex items-start gap-3" data-id="element-27">
            {/* Company Logo */}
            {company.logoUrl ? <img src={company.logoUrl} alt={`${company.name} logo`} className="w-10 h-10 rounded-lg object-contain border border-border/40 bg-white shrink-0 p-0.5" data-id="element-28" /> : <div className="w-10 h-10 rounded-lg border border-border/40 bg-muted/50 flex items-center justify-center shrink-0" data-id="element-29">
                <Building2 className="w-4.5 h-4.5 text-muted-foreground/60" data-id="element-30" />
              </div>}
            <div data-id="element-31">
              <h3 className="font-heading font-bold text-base leading-tight text-foreground tracking-tight" data-id="element-32">
                {company.name}
              </h3>
              <div className="flex items-center gap-1.5 mt-1 text-[11px] font-mono text-muted-foreground flex-wrap" data-id="element-33">
                <span className="font-medium text-foreground/70" data-id="element-34">
                  {company.industry}
                </span>
                <span className="text-border" data-id="element-35">•</span>
                <span data-id="element-36">{company.size} emp</span>
                <span className="text-border" data-id="element-37">•</span>
                <span data-id="element-38">{company.stage}</span>
                <span className="text-border" data-id="element-39">•</span>
                <span data-id="element-40">{company.funding}</span>
              </div>
            </div>
          </div>
          {/* Match Score - prominent, color-coded */}
          <div className={`${matchColor.bg} ${matchColor.text} ${matchColor.ring} ring-2 font-mono text-sm font-bold rounded-md px-2 py-1 leading-none shrink-0 flex flex-col items-center`} data-id="element-41">
            <span className="text-[15px] leading-none" data-id="element-42">
              {company.matchScore}%
            </span>
            <span className="text-[8px] uppercase tracking-wider opacity-80 mt-0.5" data-id="element-43">
              match
            </span>
          </div>
        </div>

        {/* Signals Row */}
        <div className="flex flex-wrap gap-1.5 mt-1" data-id="element-44">
          {sortedSignals.map(signal => {
          const emoji = getSignalEmoji(signal.text);
          return <Tooltip key={signal.id} data-id="element-45">
                <TooltipTrigger asChild data-id="element-46">
                  <Badge variant="outline" className={`font-mono text-[10px] px-1.5 py-0 h-5 rounded-sm border flex items-center gap-1 cursor-help transition-all duration-150 ${getSignalColor(signal.type)}`} data-id="element-47">
                    {emoji && <span className="text-[11px] leading-none" data-id="element-48">{emoji}</span>}
                    <span data-id="element-49">
                      {getSignalPrefix(signal.type)} {signal.text}
                    </span>
                    <Info className="w-2.5 h-2.5 opacity-30 group-hover:opacity-60 transition-opacity ml-0.5" data-id="element-50" />
                  </Badge>
                </TooltipTrigger>
                <TooltipContent side="bottom" align="start" className="max-w-[280px] p-3 bg-popover border border-border shadow-lg z-50" sideOffset={6} data-id="element-51">
                  <p className="text-xs leading-relaxed text-popover-foreground" data-id="element-52">
                    {signal.detail}
                  </p>
                  <div className="mt-2 pt-2 border-t border-border/50" data-id="element-53">
                    <span className="text-[10px] font-mono text-muted-foreground uppercase tracking-wider" data-id="element-54">
                      Source:
                    </span>
                    <span className="text-[10px] font-mono text-muted-foreground ml-1" data-id="element-55">
                      {signal.source}
                    </span>
                  </div>
                </TooltipContent>
              </Tooltip>;
        })}
        </div>

        {/* Review Snippet */}
        <div className="mt-auto pt-2.5 border-t border-border/40" data-id="element-56">
          <div className="flex items-start gap-2" data-id="element-57">
            <MessageSquareQuote className="w-3.5 h-3.5 mt-0.5 shrink-0 text-muted-foreground/50" data-id="element-58" />
            <div className="flex-1" data-id="element-59">
              <p className="text-[11px] italic leading-snug text-muted-foreground" data-id="element-60">
                "{company.reviewSnippet}"
              </p>
              {/* Ratings Row */}
              <div className="flex items-center gap-4 mt-2" data-id="element-61">
                {/* Teamblind */}
                <Tooltip data-id="element-62">
                  <TooltipTrigger asChild data-id="element-63">
                    <div className="flex items-center gap-1.5 cursor-help border-b border-dotted border-muted-foreground/30 pb-0.5 hover:border-muted-foreground/60 transition-colors" data-id="element-64">
                      <img src={TEAMBLIND_LOGO} alt="Teamblind" className="w-3.5 h-3.5 rounded-[2px] object-cover" data-id="element-65" />
                      <span className="text-[10px] font-mono uppercase tracking-wider font-medium text-muted-foreground" data-id="element-66">
                        Blind
                      </span>
                      <span className="text-[10px] font-mono font-semibold text-foreground bg-muted px-1 rounded-sm" data-id="element-67">
                        {company.reviewRating}/5.0
                      </span>
                    </div>
                  </TooltipTrigger>
                  {company.blindBreakdown && <TooltipContent side="top" align="start" className="p-0 bg-popover border border-border shadow-lg z-50 w-[300px]" sideOffset={8} data-id="element-68">
                      <RatingBreakdownCard breakdown={company.blindBreakdown} logo={TEAMBLIND_LOGO} name="Blind" companyName={company.name} data-id="element-69" />
                    </TooltipContent>}
                </Tooltip>
                {/* Glassdoor */}
                <Tooltip data-id="element-70">
                  <TooltipTrigger asChild data-id="element-71">
                    <div className="flex items-center gap-1.5 cursor-help border-b border-dotted border-muted-foreground/30 pb-0.5 hover:border-muted-foreground/60 transition-colors" data-id="element-72">
                      <img src={GLASSDOOR_LOGO} alt="Glassdoor" className="w-3.5 h-3.5 rounded-[2px] object-contain" data-id="element-73" />
                      <span className="text-[10px] font-mono uppercase tracking-wider font-medium text-muted-foreground" data-id="element-74">
                        Glassdoor
                      </span>
                      {company.glassdoorRating !== null ? <span className="text-[10px] font-mono font-semibold text-foreground bg-muted px-1 rounded-sm" data-id="element-75">
                          {company.glassdoorRating}/5.0
                        </span> : <span className="text-[10px] font-mono text-muted-foreground/60 italic" data-id="element-76">
                          N/A
                        </span>}
                    </div>
                  </TooltipTrigger>
                  {company.glassdoorBreakdown && <TooltipContent side="top" align="start" className="p-0 bg-popover border border-border shadow-lg z-50 w-[300px]" sideOffset={8} data-id="element-77">
                      <RatingBreakdownCard breakdown={company.glassdoorBreakdown} logo={GLASSDOOR_LOGO} name="Glassdoor" companyName={company.name} data-id="element-78" />
                    </TooltipContent>}
                </Tooltip>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="px-4 py-1 bg-muted/30 border-t border-border/40 flex justify-between items-center" data-id="element-79">
        <span className="text-[10px] font-mono text-muted-foreground" data-id="element-80">
          Updated: {company.lastUpdated}
        </span>
        <button className="text-[11px] font-mono font-medium text-primary hover:underline" data-id="element-81">
          View full report →
        </button>
      </div>
    </Card>;
}